import React, { useState, useEffect, useCallback } from "react";
import { Package, ArrowDownToLine, ArrowUpFromLine, Clock, Loader2, Plus, X, ClipboardList, BookUser } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("mv_token")}` } });

export default function CustomerWarehouse() {
  const [tab, setTab] = useState("inventory");
  const [inventory, setInventory] = useState([]);
  const [movements, setMovements] = useState([]);
  const [requests, setRequests] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [recentDests, setRecentDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);

  const load = useCallback(async () => {
    try {
      const [invR, movR, reqR, addrR, shipR, pickR] = await Promise.all([
        axios.get(`${API}/warehouse/inventory`, authH()),
        axios.get(`${API}/warehouse/movements`, authH()),
        axios.get(`${API}/warehouse/requests`, authH()),
        axios.get(`${API}/addresses`, authH()),
        axios.get(`${API}/shipments`, authH()),
        axios.get(`${API}/pickups`, authH()),
      ]);
      setInventory(invR.data);
      setMovements(movR.data);
      setRequests(reqR.data);
      setAddresses(addrR.data || []);

      // Build unique destinations from shipments + pickups
      const destMap = new Map();
      for (const s of [...(shipR.data || []), ...(pickR.data || [])]) {
        const d = s.dest_details;
        if (d && d.city) {
          const key = `${d.name}-${d.city}-${d.address}`;
          if (!destMap.has(key)) destMap.set(key, d);
        }
      }
      setRecentDests(Array.from(destMap.values()));
    } catch { /* service not enabled */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-40"><Loader2 className="animate-spin text-[#FF5200]" size={28} /></div>;

  const TABS = [
    { id: "inventory", label: "Inventario", icon: Package, count: inventory.length },
    { id: "movements", label: "Movimenti", icon: ArrowDownToLine, count: movements.length },
    { id: "requests", label: "Le Mie Richieste", icon: Clock, count: requests.length },
  ];

  return (
    <div className="space-y-4" data-testid="customer-warehouse">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${tab === t.id ? "bg-[#FF5200] text-white" : "text-zinc-500 hover:text-[#09090B]"}`}>
              <t.icon size={14} /> {t.label} {t.count > 0 && <span className="ml-1 text-[10px] opacity-70">({t.count})</span>}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={() => setShowRequest(true)} data-testid="new-request-btn"
          className="flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 rounded-sm text-xs font-bold uppercase hover:bg-[#e64a00] transition-colors">
          <Plus size={14} /> Richiedi Uscita Merce
        </button>
      </div>

      {tab === "inventory" && (
        inventory.length === 0 ? <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-zinc-400 text-sm">Nessun articolo in magazzino</div> : (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="client-inventory-table">
              <thead><tr className="bg-[#09090B] text-white text-xs uppercase tracking-wider">
                <th className="px-4 py-2.5 text-left">Articolo</th>
                <th className="px-4 py-2.5 text-right">Quantita</th>
                <th className="px-4 py-2.5 text-left">Unita</th>
                <th className="px-4 py-2.5 text-left">Posizione</th>
              </tr></thead>
              <tbody>{inventory.map((item, i) => (
                <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2.5 font-semibold">{item.item_name}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-lg">{item.quantity}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{item.unit || "pz"}</td>
                  <td className="px-4 py-2.5"><span className="bg-zinc-100 px-2 py-0.5 rounded text-xs font-mono">{item.location_code || "-"}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}

      {tab === "movements" && (
        movements.length === 0 ? <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-zinc-400 text-sm">Nessun movimento</div> : (
          <div className="space-y-2">
            {movements.map((m, i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-sm px-4 py-3 flex items-center gap-3">
                <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${m.movement_type === "ingresso" ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
                  {m.movement_type === "ingresso" ? <ArrowDownToLine size={14} /> : <ArrowUpFromLine size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{m.movement_type === "ingresso" ? "INGRESSO" : "USCITA"}</p>
                  <p className="text-xs text-zinc-400">{m.description || m.reference || "-"} | {m.items?.length || 0} art. | {m.created_at?.slice(0,10)}</p>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "requests" && (
        requests.length === 0 ? <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-zinc-400 text-sm">Nessuna richiesta inviata</div> : (
          <div className="space-y-2">
            {requests.map((r, i) => {
              const statusC = { in_attesa: "bg-amber-100 text-amber-700", approvato: "bg-green-100 text-green-700", rifiutato: "bg-red-100 text-red-600" };
              const statusL = { in_attesa: "In Attesa", approvato: "Approvato", rifiutato: "Rifiutato" };
              return (
                <div key={i} className="bg-white border border-zinc-200 rounded-sm px-4 py-3 flex items-center gap-3">
                  <div className="bg-amber-100 text-amber-600 w-7 h-7 rounded-sm flex items-center justify-center"><Clock size={14} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{r.request_type === "uscita" ? "Uscita Merce" : "Preparazione"}</p>
                    <p className="text-xs text-zinc-400">{r.items?.length || 0} articoli | {r.destination?.city || ""} | {r.created_at?.slice(0,10)}</p>
                  </div>
                  <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-sm ${statusC[r.status] || "bg-zinc-100"}`}>{statusL[r.status] || r.status}</span>
                </div>
              );
            })}
          </div>
        )
      )}

      {showRequest && <RequestForm inventory={inventory} addresses={addresses} recentDests={recentDests} onClose={() => setShowRequest(false)} onSave={load} />}
    </div>
  );
}

function RequestForm({ inventory, addresses, recentDests, onClose, onSave }) {
  const [form, setForm] = useState({
    request_type: "uscita",
    items: [{ name: "", quantity: 1, unit: "pz" }],
    destination_name: "", destination_company: "", destination_address: "",
    destination_city: "", destination_cap: "", destination_province: "", destination_phone: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const [showRubrica, setShowRubrica] = useState(false);
  const [saveToBook, setSaveToBook] = useState(false);

  const alreadyInBook = addresses.some(a =>
    a.type === "destinatario" &&
    a.name?.trim().toLowerCase() === form.destination_name?.trim().toLowerCase() &&
    a.address?.trim().toLowerCase() === form.destination_address?.trim().toLowerCase() &&
    a.city?.trim().toLowerCase() === form.destination_city?.trim().toLowerCase()
  );

  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", quantity: 1, unit: "pz" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, value) => { const items = [...form.items]; items[i] = { ...items[i], [field]: value }; setForm({ ...form, items }); };

  const applyAddress = (addr) => {
    setForm({
      ...form,
      destination_name: addr.name || "",
      destination_company: addr.company || "",
      destination_address: addr.address || "",
      destination_city: addr.city || "",
      destination_cap: addr.cap || "",
      destination_province: addr.province || "",
      destination_phone: addr.phone || "",
    });
    setShowRubrica(false);
    toast.success("Indirizzo applicato");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.items.some(i => i.name && i.quantity > 0)) return toast.error("Aggiungi almeno un articolo");
    setSaving(true);
    try {
      await axios.post(`${API}/warehouse/requests`, form, authH());
      // Save destination to address book if requested
      if (saveToBook && !alreadyInBook && form.destination_name && form.destination_address && form.destination_city) {
        try {
          await axios.post(`${API}/addresses`, {
            type: "destinatario",
            name: form.destination_name,
            company: form.destination_company || "",
            address: form.destination_address,
            city: form.destination_city,
            cap: form.destination_cap || "",
            province: form.destination_province || "",
            phone: form.destination_phone || "",
          }, authH());
          toast.success("Indirizzo salvato in rubrica");
        } catch { /* non-blocking */ }
      }
      toast.success("Richiesta inviata con successo");
      onClose(); onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const allAddresses = [
    ...addresses.map(a => ({ ...a, _source: "rubrica" })),
    ...recentDests.map(d => ({ ...d, _source: "recente" })),
  ];

  const inputCls = "w-full border border-zinc-200 rounded-sm px-2.5 py-2 text-sm focus:border-[#FF5200] outline-none";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-4 overflow-y-auto">
      <div className="bg-white rounded-sm w-full max-w-xl shadow-xl m-4" data-testid="client-request-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <h2 className="font-heading font-bold text-base uppercase tracking-wide">Richiesta Uscita Merce</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Articoli */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Articoli *</label>
            {form.items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 bg-zinc-50 p-2 rounded-sm mb-2">
                <select value={item.name} onChange={e => updateItem(i, "name", e.target.value)} data-testid={`req-item-${i}`}
                  className="flex-1 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm">
                  <option value="">-- Seleziona --</option>
                  {inventory.map(inv => <option key={inv.item_name} value={inv.item_name}>{inv.item_name} (disp: {inv.quantity})</option>)}
                </select>
                <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, "quantity", parseInt(e.target.value) || 0)}
                  className="w-20 border border-zinc-200 rounded-sm px-2 py-1.5 text-sm text-center" />
                {form.items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="p-1 text-zinc-300 hover:text-red-500"><X size={14} /></button>}
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-xs text-[#FF5200] font-bold flex items-center gap-1"><Plus size={12} /> Aggiungi articolo</button>
          </div>

          {/* Destinatario */}
          <fieldset className="border border-zinc-200 rounded-sm p-4">
            <legend className="text-xs font-bold uppercase text-zinc-500 px-2 flex items-center gap-2">
              Destinatario
              {allAddresses.length > 0 && (
                <button type="button" onClick={() => setShowRubrica(!showRubrica)} data-testid="toggle-rubrica-btn"
                  className="text-[#FF5200] flex items-center gap-1 ml-2 normal-case font-bold">
                  <BookUser size={12} /> {showRubrica ? "Chiudi Rubrica" : "Scegli dalla Rubrica"}
                </button>
              )}
            </legend>

            {/* Address book selector */}
            {showRubrica && (
              <div className="mb-4 max-h-48 overflow-y-auto border border-zinc-100 rounded-sm bg-zinc-50" data-testid="rubrica-list">
                {allAddresses.length === 0 ? (
                  <p className="p-3 text-xs text-zinc-400 text-center">Nessun indirizzo salvato</p>
                ) : allAddresses.map((a, i) => (
                  <button key={i} type="button" onClick={() => applyAddress(a)} data-testid={`rubrica-item-${i}`}
                    className="w-full text-left px-3 py-2.5 border-b border-zinc-100 hover:bg-white transition-colors last:border-b-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${a._source === "rubrica" ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"}`}>
                        {a._source === "rubrica" ? "Rubrica" : "Recente"}
                      </span>
                      <span className="text-sm font-semibold truncate">{a.name || a.company || "-"}</span>
                      {a.company && a.name && <span className="text-xs text-zinc-400">({a.company})</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">
                      {[a.address, a.cap, a.city, a.province ? `(${a.province})` : ""].filter(Boolean).join(", ")}
                    </p>
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Nome / Ragione Sociale *</label>
                <input value={form.destination_name} onChange={e => setForm({ ...form, destination_name: e.target.value })} placeholder="Mario Rossi" className={inputCls} data-testid="dest-name" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Azienda</label>
                <input value={form.destination_company} onChange={e => setForm({ ...form, destination_company: e.target.value })} placeholder="Rossi SRL" className={inputCls} data-testid="dest-company" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Indirizzo *</label>
                <input value={form.destination_address} onChange={e => setForm({ ...form, destination_address: e.target.value })} placeholder="Via Roma 1" className={inputCls} data-testid="dest-address" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Citta *</label>
                <input value={form.destination_city} onChange={e => setForm({ ...form, destination_city: e.target.value })} placeholder="Milano" className={inputCls} data-testid="dest-city" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">CAP</label>
                <input value={form.destination_cap} onChange={e => setForm({ ...form, destination_cap: e.target.value })} placeholder="20100" className={inputCls} data-testid="dest-cap" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Provincia</label>
                <input value={form.destination_province} onChange={e => setForm({ ...form, destination_province: e.target.value })} placeholder="MI" className={inputCls} data-testid="dest-province" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase text-zinc-400 mb-1">Telefono</label>
                <input value={form.destination_phone} onChange={e => setForm({ ...form, destination_phone: e.target.value })} placeholder="+39 333 1234567" className={inputCls} data-testid="dest-phone" />
              </div>
            </div>

            {/* Save to address book */}
            <div className="mt-4 pt-3 border-t border-zinc-100">
              {alreadyInBook ? (
                <p className="text-xs text-zinc-400 italic flex items-center gap-1.5">
                  <BookUser size={12} /> Indirizzo gia presente in rubrica
                </p>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer group" data-testid="save-warehouse-dest-to-book">
                  <input type="checkbox" checked={saveToBook} onChange={e => setSaveToBook(e.target.checked)}
                    className="w-4 h-4 accent-[#FF5200]" />
                  <span className="text-xs font-bold uppercase tracking-wide text-zinc-600 group-hover:text-[#FF5200] flex items-center gap-1.5">
                    <BookUser size={14} /> Salva in rubrica per usarlo di nuovo
                  </span>
                </label>
              )}
            </div>
          </fieldset>

          {/* Note */}
          <div>
            <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Note</label>
            <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Indicazioni particolari..."
              className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm h-16 resize-none focus:border-[#FF5200] outline-none" />
          </div>

          <button type="submit" disabled={saving} data-testid="req-save-btn"
            className="w-full bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />} Invia Richiesta
          </button>
        </form>
      </div>
    </div>
  );
}
